import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Button, CardContent, Grid, Typography, makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { CardPageItem, PageItem } from "../../../custom/Page";
import { createInvite, searchInvites } from "../../../actions/Invite";
import { showErrorMessage, showSuccessMessage } from "../../../actions/Alert";
import { getMe } from "../../../actions/User";
import { Permissions, resolveErrorMessage } from "../../../util";
import useHasPermissions from "../../../hooks/useHasPermission";
import DebouncedField from "../DebouncedField";
import LoadingSpinner from "../LoadingSpinner";
import NewInviteDialog, { normalizeInvitePayload } from "./NewInviteDialog";
import { InviteListBySituationWithAccordion } from "./InvitesBySituation";

const useStyles = makeStyles((theme) => ({
  filterCard: {
    marginBottom: theme.spacing(1),
  },
}));

const InvitesContent = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { hasPermission } = useHasPermissions();
  const canManageInvites = hasPermission(Permissions.EDIT_INVITATIONS);

  const [filter, setFilter] = useState({ searchByText: "" });
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchInvites = useCallback(async (filterParams) => {
    setLoading(true);
    try {
      const data = await searchInvites({
        ...(filterParams.searchByText && { nameOrEmail: filterParams.searchByText }),
      });
      setInvites(data?.content || []);
    } catch (error) {
      dispatch(showErrorMessage(resolveErrorMessage(error)));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchInvites({ searchByText: "" });
  }, [fetchInvites]);

  const handleFilterChange = useCallback((name, value) => {
    const nextFilter = { ...filter, [name]: value };
    setFilter(nextFilter);
    fetchInvites(nextFilter);
  }, [fetchInvites, filter]);

  const handleCreateInvite = async (values) => {
    try {
      await createInvite(normalizeInvitePayload(values));
      dispatch(showSuccessMessage("app.invite.create.success"));
      setDialogOpen(false);
      const nextFilter = { searchByText: "" };
      setFilter(nextFilter);
      fetchInvites(nextFilter);
    } catch (error) {
      if (error?.response?.status === 400) dispatch(getMe());
      dispatch(showErrorMessage(resolveErrorMessage(error)));
    }
  };

  const hasFilters = React.useMemo(() => filter.searchByText?.length > 0 ,[filter.searchByText]);

  const invitesByStatus = useMemo(() => {
    const groups = { ACTIVE: [], USED: [], EXPIRED: [], REVOKED: [] };
    invites.forEach((invite) => {
      (groups[invite.status] || groups.ACTIVE).push(invite);
    });
    return groups;
  }, [invites]);

  const handleInviteChanged = (updatedInvite) => {
    setInvites((prev) => prev.map((invite) => (invite.id === updatedInvite.id ? { ...invite, ...updatedInvite } : invite)));
  };

  const handleOpenCreateDialog = () => {
    if (!canManageInvites) {
      dispatch(showErrorMessage("app.inviteToken.create.permissionDenied"));
      return;
    }
    setDialogOpen(true);
  };

  return (
    <PageItem>
      <CardPageItem raised margin={1} className={classes.filterCard}>
        <CardContent>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={8} sm={8} md={8}>
              <DebouncedField
                id="searchByText"
                name="searchByText"
                label={<FormattedMessage id="app.search.nameemail" />}
                value={filter.searchByText}
                onDebouncedChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={4} sm={4} md={4}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
              >
                <FormattedMessage id="app.invite.create" />
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </CardPageItem>

      {loading ? (
        <LoadingSpinner />
      ) : invites.length === 0 ? (
        <CardPageItem raised margin={1}>
          <CardContent>
            <Typography align="center">
              <FormattedMessage id="app.invite.empty" />
            </Typography>
          </CardContent>
        </CardPageItem>
      ) : (
        <>
          <InviteListBySituationWithAccordion
            data={invitesByStatus.ACTIVE}
            textBar="app.invite.situation.pending"
            defaultOpen
            canManage={canManageInvites}
            onInviteChanged={handleInviteChanged}
          />
          <InviteListBySituationWithAccordion
            data={invitesByStatus.USED}
            textBar="app.invite.situation.used"
            defaultOpen={hasFilters}
            canManage={canManageInvites}
            onInviteChanged={handleInviteChanged}
          />
          <InviteListBySituationWithAccordion
            data={invitesByStatus.EXPIRED}
            textBar="app.invite.situation.expired"
            defaultOpen={hasFilters}
            canManage={canManageInvites}
            onInviteChanged={handleInviteChanged}
          />
          <InviteListBySituationWithAccordion
            data={invitesByStatus.REVOKED}
            textBar="app.invite.situation.revoked"
            defaultOpen={hasFilters}
            canManage={canManageInvites}
            onInviteChanged={handleInviteChanged}
          />
        </>
      )}

      {dialogOpen && (
        <NewInviteDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleCreateInvite}
        />
      )}
    </PageItem>
  );
};

export default InvitesContent;
